import React, { useState, useEffect, useRef } from 'react';
import { Music, Sparkles, Sliders, Check, Loader2, Volume2, ChevronDown } from 'lucide-react';
import { AudioEngine } from '../core/audioEngine';
import { InstrumentId, InstrumentOption, InstrumentStatus, INSTRUMENT_OPTIONS } from '../audio/types';

interface InstrumentSelectorProps {
  audioEngine: AudioEngine;
  className?: string;
}

export const InstrumentSelector: React.FC<InstrumentSelectorProps> = ({ audioEngine, className = '' }) => {
  const [currentId, setCurrentId] = useState<InstrumentId>(() => audioEngine.getInstrumentId());
  const [statuses, setStatuses] = useState<Record<InstrumentId, InstrumentStatus>>(() => ({
    'acoustic-grand': audioEngine.getInstrumentStatus('acoustic-grand'),
    'upright-piano': audioEngine.getInstrumentStatus('upright-piano'),
    'electric-piano': audioEngine.getInstrumentStatus('electric-piano'),
    'synth-piano': audioEngine.getInstrumentStatus('synth-piano'),
  }));
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync with audio engine status events
  useEffect(() => {
    setCurrentId(audioEngine.getInstrumentId());

    const unsubscribe = audioEngine.onInstrumentStatusChange((status) => {
      setStatuses((prev) => ({
        ...prev,
        [status.id]: status,
      }));
      setCurrentId(audioEngine.getInstrumentId());
    });

    return unsubscribe;
  }, [audioEngine]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentOption = INSTRUMENT_OPTIONS.find((opt) => opt.id === currentId) || INSTRUMENT_OPTIONS[0];
  const currentStatus = statuses[currentId];

  const handleSelect = (id: InstrumentId) => {
    audioEngine.setInstrument(id);
    setCurrentId(id);
    setIsOpen(false);
    // Audition C4 note on selection
    audioEngine.playNote(60, 95, 0.4);
  };

  const handleAudition = (e: React.MouseEvent, id: InstrumentId) => {
    e.stopPropagation();
    // Temporarily audition
    const prev = audioEngine.getInstrumentId();
    audioEngine.setInstrument(id);
    audioEngine.playNote(60, 100, 0.5);
    if (prev !== id) {
      setTimeout(() => {
        audioEngine.setInstrument(prev);
      }, 600);
    }
  };

  const getCategoryIcon = (category: InstrumentOption['category']) => {
    switch (category) {
      case 'acoustic':
        return <Music className="w-3.5 h-3.5 text-amber-400" />;
      case 'electric':
        return <Sparkles className="w-3.5 h-3.5 text-cyan-400" />;
      case 'modeled':
        return <Sliders className="w-3.5 h-3.5 text-indigo-400" />;
    }
  };

  return (
    <div ref={dropdownRef} className={`relative inline-block text-left ${className}`}>
      {/* Trigger Button */}
      <button
        id="instrument-selector-btn"
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 px-2.5 h-7 bg-[#101b24] hover:bg-[#152330] border border-cyan-900/60 hover:border-cyan-700 text-xs text-cyan-100 transition-all shadow-xs rounded-xs"
        title="Choose virtual instrument sound engine"
      >
        <div className="flex items-center gap-1.5">
          {getCategoryIcon(currentOption.category)}
          <span className="font-semibold text-cyan-50 max-w-[130px] sm:max-w-[150px] truncate">
            {currentOption.name}
          </span>
        </div>

        {/* Loading progress if downloading samples */}
        {currentStatus?.state === 'loading' && currentStatus.progress !== undefined && currentStatus.progress < 100 && (
          <span className="flex items-center gap-1 text-[10px] text-[#F5C767] bg-[#F5C767]/15 border border-[#F5C767]/40 px-1.5 py-0.5 font-mono">
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
            <span>{currentStatus.progress}%</span>
          </span>
        )}

        <ChevronDown className={`w-3 h-3 text-[#A89F91] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          id="instrument-dropdown-panel"
          className="absolute right-0 sm:left-0 sm:right-auto mt-1.5 w-72 bg-[#181614] border border-[#3D362C] shadow-2xl py-1.5 z-50 overflow-hidden text-[#EBE6DB]"
        >
          <div className="px-3 py-1.5 border-b border-[#2D2820] flex items-center justify-between bg-[#141210]">
            <span className="text-[10px] uppercase font-bold tracking-wider text-[#A89F91]">
              Virtual Instrument Engine
            </span>
            <span className="text-[10px] text-[#F5C767] font-semibold">Web Audio + Samples</span>
          </div>

          <div className="p-1 space-y-1">
            {INSTRUMENT_OPTIONS.map((opt) => {
              const isSelected = opt.id === currentId;
              const status = statuses[opt.id];
              const isLoading = status?.state === 'loading';
              const progress = status?.progress ?? 0;

              return (
                <div
                  key={opt.id}
                  id={`instrument-opt-${opt.id}`}
                  onClick={() => handleSelect(opt.id)}
                  className={`group relative flex items-start gap-2.5 p-2 cursor-pointer transition-all border ${
                    isSelected
                      ? 'bg-[#25211B] border-[#F5C767] text-[#F5C767] shadow-xs'
                      : 'hover:bg-[#25211B] text-[#A89F91] hover:text-[#EBE6DB] border-transparent'
                  }`}
                >
                  <div className="mt-0.5 p-1 bg-[#141210] border border-[#3D362C]">
                    {getCategoryIcon(opt.category)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-xs font-bold truncate text-[#EBE6DB] group-hover:text-[#F5C767] transition-colors">{opt.name}</span>
                      <span
                        className={`text-[9px] px-1.5 py-0.2 border font-mono ${
                          opt.category === 'acoustic'
                            ? 'bg-[#F5C767]/15 text-[#F5C767] border-[#F5C767]/40'
                            : opt.category === 'electric'
                            ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40'
                            : 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                        }`}
                      >
                        {opt.badge}
                      </span>
                    </div>

                    <p className="text-[10px] text-[#A89F91] leading-tight mt-0.5 line-clamp-2">
                      {opt.description}
                    </p>

                    {/* Progress Bar for downloading samples */}
                    {isLoading && progress < 100 && (
                      <div className="mt-1.5">
                        <div className="flex items-center justify-between text-[9px] text-[#F5C767] font-mono mb-0.5">
                          <span className="flex items-center gap-1">
                            <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            Loading acoustic samples...
                          </span>
                          <span>{progress}%</span>
                        </div>
                        <div className="w-full h-1 bg-[#141210] overflow-hidden border border-[#3D362C]">
                          <div
                            className="h-full bg-[#F5C767] transition-all duration-200"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions / Selection check */}
                  <div className="flex items-center gap-1 mt-0.5">
                    <button
                      type="button"
                      onClick={(e) => handleAudition(e, opt.id)}
                      className="p-1 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#141210] transition-colors"
                      title="Audition C4 note"
                    >
                      <Volume2 className="w-3 h-3" />
                    </button>
                    {isSelected && <Check className="w-3.5 h-3.5 text-[#F5C767] ml-0.5 shrink-0" />}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-3 py-1.5 bg-[#141210] border-t border-[#2D2820] text-[10px] text-[#A89F91]/70 flex items-center justify-between">
            <span>IndexedDB Caching: Active</span>
            <span>480 PPQ Polyphonic</span>
          </div>
        </div>
      )}
    </div>
  );
};
